import React, { useState } from "react";

interface PasswordGeneratorProps {
  onRunRecorded?: (input: Record<string, unknown>, output: unknown) => void;
}

const UPPERCASE = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const LOWERCASE = "abcdefghijklmnopqrstuvwxyz";
const NUMBERS = "0123456789";
const SYMBOLS = "!@#$%^&*()_+-=[]{}|;:,.<>?";

function generatePassword(opts: {
  length: number;
  upper: boolean;
  lower: boolean;
  numbers: boolean;
  symbols: boolean;
}): string {
  let charset = "";
  const mandatory: string[] = [];

  if (opts.upper) { charset += UPPERCASE; mandatory.push(UPPERCASE[Math.floor(Math.random() * UPPERCASE.length)]); }
  if (opts.lower) { charset += LOWERCASE; mandatory.push(LOWERCASE[Math.floor(Math.random() * LOWERCASE.length)]); }
  if (opts.numbers) { charset += NUMBERS; mandatory.push(NUMBERS[Math.floor(Math.random() * NUMBERS.length)]); }
  if (opts.symbols) { charset += SYMBOLS; mandatory.push(SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)]); }

  if (!charset) return "";

  const array = new Uint32Array(opts.length);
  crypto.getRandomValues(array);
  const chars = Array.from(array).map((n) => charset[n % charset.length]);

  // Inject mandatory chars at random positions to guarantee at least one of each
  mandatory.forEach((c, i) => { chars[i % opts.length] = c; });

  // Fisher-Yates shuffle using crypto random values
  const shuffleArr = new Uint32Array(opts.length);
  crypto.getRandomValues(shuffleArr);
  for (let i = chars.length - 1; i > 0; i--) {
    const j = shuffleArr[i] % (i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }

  return chars.join("");
}

function passwordStrength(password: string): { label: string; color: string; score: number } {
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (password.length >= 16) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score <= 2) return { label: "Weak", color: "bg-red-500", score };
  if (score <= 4) return { label: "Fair", color: "bg-yellow-400", score };
  if (score <= 5) return { label: "Good", color: "bg-blue-500", score };
  return { label: "Strong", color: "bg-green-500", score };
}

export default function PasswordGeneratorTool({ onRunRecorded }: PasswordGeneratorProps) {
  const [length, setLength] = useState(16);
  const [upper, setUpper] = useState(true);
  const [lower, setLower] = useState(true);
  const [numbers, setNumbers] = useState(true);
  const [symbols, setSymbols] = useState(true);
  const [count, setCount] = useState(5);
  const [passwords, setPasswords] = useState<string[]>([]);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const generate = () => {
    setError(null);
    if (!upper && !lower && !numbers && !symbols) {
      setError("Select at least one character type.");
      return;
    }
    const opts = { length, upper, lower, numbers, symbols };
    const generated = Array.from({ length: count }, () => generatePassword(opts));
    setPasswords(generated);
    onRunRecorded?.(
      { length, upper, lower, numbers, symbols, count },
      { passwords: generated },
    );
  };

  const copy = (pw: string, idx: number) => {
    navigator.clipboard.writeText(pw).then(() => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1500);
    });
  };

  const Toggle = ({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) => (
    <label className="flex items-center gap-2 cursor-pointer select-none">
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={`relative w-10 h-5 rounded-full transition-colors ${value ? "bg-indigo-600" : "bg-gray-300"}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${value ? "translate-x-5" : ""}`} />
      </button>
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  );

  return (
    <div className="space-y-6">
      {/* Options */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Length: {length}</label>
          <input
            type="range"
            min={4}
            max={128}
            value={length}
            onChange={(e) => setLength(Number(e.target.value))}
            className="w-full accent-indigo-600"
          />
          <div className="flex justify-between text-xs text-gray-400 mt-0.5"><span>4</span><span>128</span></div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Count: {count}</label>
          <input
            type="range"
            min={1}
            max={20}
            value={count}
            onChange={(e) => setCount(Number(e.target.value))}
            className="w-full accent-indigo-600"
          />
          <div className="flex justify-between text-xs text-gray-400 mt-0.5"><span>1</span><span>20</span></div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Toggle label="Uppercase (A-Z)" value={upper} onChange={setUpper} />
        <Toggle label="Lowercase (a-z)" value={lower} onChange={setLower} />
        <Toggle label="Numbers (0-9)" value={numbers} onChange={setNumbers} />
        <Toggle label="Symbols (!@#…)" value={symbols} onChange={setSymbols} />
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      <button
        onClick={generate}
        className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition"
      >
        Generate Passwords
      </button>

      {passwords.length > 0 && (
        <div className="space-y-2">
          {passwords.map((pw, idx) => {
            const strength = passwordStrength(pw);
            return (
              <div key={idx} className="border rounded-lg px-4 py-3 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <code className="text-sm font-mono break-all text-gray-800">{pw}</code>
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5,6,7].map((s) => (
                        <div key={s} className={`h-1 w-4 rounded-full ${s <= strength.score ? strength.color : "bg-gray-200"}`} />
                      ))}
                    </div>
                    <span className="text-xs text-gray-500">{strength.label}</span>
                    <span className="text-xs text-gray-400">{pw.length} chars</span>
                  </div>
                </div>
                <button
                  onClick={() => copy(pw, idx)}
                  className="shrink-0 text-xs px-3 py-1.5 border rounded-lg hover:bg-gray-50 transition font-medium"
                >
                  {copiedIdx === idx ? "Copied!" : "Copy"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
